import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { useMediaQuery } from '@/hooks/use-media-query';
import { useHuntStore } from '@/stores/hunt-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { Button } from '@/components/common/Button';
import { InlineErrorBanner } from '@/components/common/InlineErrorBanner';
import { PhotoGallery } from '@/components/photos/PhotoGallery';
import { PhotoPicker } from '@/components/photos/PhotoPicker';
import { DateField } from '@/components/forms/DateField';
import { TimeField } from '@/components/forms/TimeField';
import { addPhotosToSit, getPhotosBySitId, deleteSitPhoto } from '@/services/photo-service';
import type { PhotoRecord } from '@/types/photo';
import type { HuntSit } from '@/types/hunt';
import { localTodayISO } from '@/utils/date-utils';

// M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
const INPUT_CLASS =
  'w-full min-w-0 box-border px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px]';

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="field-label">
      {children}
      {required && <span className="text-[var(--color-accent)] ml-1">*</span>}
    </label>
  );
}

export interface LogSitSheetProps {
  huntId: string;
  propertyId: string;
  onClose: () => void;
  /** When provided, the sheet operates in edit mode pre-populated with this sit's data. */
  existingSit?: HuntSit;
}

/**
 * Log a Sit bottom sheet — extracted from HuntDetailPage in Defect Batch M-09
 * so the Dashboard "Log a Sit" CTA can open the same sheet inline rather than
 * navigating to the Hunt Journal first. Markup, behavior, and data-testids
 * are preserved verbatim from the previous inline definition.
 */
export function LogSitSheet({ huntId, propertyId, onClose, existingSit }: LogSitSheetProps) {
  const { createSit, updateSit, loadSitsForHunt } = useHuntStore();
  const { waypoints: locations, loadWaypoints: loadLocations } = useWaypointStore();
  const isEditMode = existingSit !== undefined;
  // Responsive time row layout: "side-by-side" at ≥640px, "stacked" below (J-06)
  const isWide = useMediaQuery('(min-width: 640px)');

  const today = localTodayISO();

  const [waypointId, setWaypointId] = useState(existingSit?.waypointId ?? '');
  const [date, setDate] = useState(existingSit?.date ?? today);
  const [period, setPeriod] = useState<'morning' | 'evening' | 'all-day'>(existingSit?.period ?? 'morning');
  const [timeIn, setTimeIn] = useState(existingSit?.timeIn ?? '');
  const [timeOut, setTimeOut] = useState(existingSit?.timeOut ?? '');
  const [notes, setNotes] = useState(existingSit?.notes ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [pendingPhotoFiles, setPendingPhotoFiles] = useState<File[]>([]);
  const [sitSaveError, setSitSaveError] = useState<string | null>(null);
  // Existing photos for edit mode — shown in gallery
  const [editModePhotos, setEditModePhotos] = useState<PhotoRecord[]>([]);

  useEffect(() => {
    void loadLocations(propertyId);
  }, [propertyId, loadLocations]);

  // Load existing photos when in edit mode
  useEffect(() => {
    if (!isEditMode || !existingSit) return;
    getPhotosBySitId(existingSit.id)
      .then((photos) => setEditModePhotos(photos))
      .catch((err) => console.error('[LogSitSheet] Failed to load sit photos:', err));
  }, [isEditMode, existingSit]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!waypointId) errs['waypointId'] = 'Location is required';
    if (!date) errs['date'] = 'Date is required';
    if (timeIn && timeOut && timeOut <= timeIn) {
      errs['timeOut'] = 'Time out must be after time in';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      if (isEditMode && existingSit) {
        await updateSit(existingSit.id, {
          waypointId,
          date,
          period,
          timeIn: timeIn || undefined,
          timeOut: timeOut || undefined,
          notes: notes.trim() || undefined,
        });
        // Reload sits so the parent list reflects the updated sit immediately.
        await loadSitsForHunt(huntId);
      } else {
        const sit = await createSit({
          huntId,
          waypointId,
          date,
          period,
          timeIn: timeIn || undefined,
          timeOut: timeOut || undefined,
          notes: notes.trim() || undefined,
        });
        // Attach any pending photos to the new sit
        if (pendingPhotoFiles.length > 0) {
          await addPhotosToSit(sit.id, huntId, sit.userId, pendingPhotoFiles);
          // Reload sits to propagate the sit's bumped updatedAt into the store,
          // which triggers useSitPhotos in SitCard to re-fetch the new photos.
          await loadSitsForHunt(huntId);
        }
      }
      setSitSaveError(null);
      onClose();
    } catch (err) {
      console.error('[LogSitSheet] Failed to save sit:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save sit. Please try again.';
      setSitSaveError(msg);
      setIsSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
      data-testid="log-sit-sheet"
    >
      {/* hypothesis (fallback artifacts) — verify on device: explicit min-w-0 on the modal lets the parent's flex-justify-center participate in shrinking even with overflow-x-hidden already present (L-04) */}
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 pb-24 sm:pb-6 flex flex-col gap-5 max-h-[85vh] overflow-y-auto overflow-x-hidden min-w-0">
        {/* Sheet header */}
        <div className="flex items-center justify-between">
          <p className="font-display text-display-sm text-[var(--color-text-display)]">
            {isEditMode ? 'Edit Sit' : 'Log a Sit'}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* hypothesis (fallback artifacts) — verify on device: min-w-0 w-full on the modal form lets it shrink properly inside the modal column on iOS Safari PWA (L-04) */}
        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4 min-w-0 w-full">
          {/* Location */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="sit-location" required>
              Location
            </FieldLabel>
            <select
              id="sit-location"
              value={waypointId}
              onChange={(e) => setWaypointId(e.target.value)}
              className={INPUT_CLASS}
              data-testid="select-location"
            >
              <option value="">— Select location —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            {errors['waypointId'] && (
              <p className="text-body-xs text-[var(--color-accent)]">{errors['waypointId']}</p>
            )}
          </div>

          {/* Date */}
          <DateField id="sit-date" label="Date" value={date} onChange={setDate} required />

          {/* Period */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Period</FieldLabel>
            <div className="grid grid-cols-3 gap-1 bg-[var(--color-elevated)] p-1 rounded-md">
              {(['morning', 'evening', 'all-day'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`py-1.5 rounded text-body-xs font-medium transition-colors capitalize ${
                    period === p
                      ? 'bg-[var(--color-surface)] text-[var(--color-text-primary)] shadow-sm border border-[var(--color-border)]'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                  }`}
                  data-testid={`period-${p}`}
                >
                  {p === 'all-day' ? 'All Day' : p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Time in / out — grid-cols-1 on mobile, 2 on sm+ (J-02) */}
          {/* hypothesis (fallback artifacts) — verify on device: gap-5 on mobile aligns vertical rhythm with form's gap-4 sibling spacing; sm:gap-3 keeps tighter horizontal gap when side-by-side (L-02 parity) */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-3 min-w-0"
            data-time-layout={isWide ? 'side-by-side' : 'stacked'}
          >
            <div className="min-w-0">
              <TimeField id="sit-time-in" label="Time In" value={timeIn} onChange={setTimeIn} />
            </div>
            <div className="min-w-0">
              <TimeField id="sit-time-out" label="Time Out" value={timeOut} onChange={setTimeOut} />
              {errors['timeOut'] && (
                <p className="text-body-xs text-[var(--color-accent)] mt-1">{errors['timeOut']}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="sit-notes">Notes</FieldLabel>
            <textarea
              id="sit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Conditions, observations…"
              className={`${INPUT_CLASS} resize-y`}
              data-testid="input-sit-notes"
            />
          </div>

          {/* Photos */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Photos</FieldLabel>
            {isEditMode && existingSit ? (
              // Edit mode — show existing photos + immediate-save picker for new ones
              <>
                {editModePhotos.length > 0 && (
                  <div className="mb-2" data-testid="sit-existing-photos">
                    <PhotoGallery
                      photos={editModePhotos}
                      onDelete={(photoId) => {
                        void deleteSitPhoto(photoId)
                          .then(() =>
                            setEditModePhotos((prev) => prev.filter((p) => p.id !== photoId)),
                          )
                          .catch((err) =>
                            console.error('[LogSitSheet] Failed to delete photo:', err),
                          );
                      }}
                      editable
                    />
                  </div>
                )}
                <PhotoPicker
                  entityType="sit"
                  entityId={existingSit.id}
                  huntId={huntId}
                  existingCount={editModePhotos.length}
                  onPhotosAdded={(newPhotos) =>
                    setEditModePhotos((prev) => [...prev, ...newPhotos])
                  }
                  disabled={isSaving}
                />
              </>
            ) : (
              // Create mode — deferred save after sit is created
              <PhotoPicker
                entityType="sit"
                entityId=""
                existingCount={pendingPhotoFiles.length}
                onPhotosAdded={() => {
                  // Immediate mode not used here — photos saved after sit creation
                }}
                onAddFiles={(files) => setPendingPhotoFiles((prev) => [...prev, ...files])}
                disabled={isSaving}
              />
            )}
          </div>

          {sitSaveError !== null && (
            <InlineErrorBanner message={sitSaveError} data-testid="sit-save-error" />
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isSaving}
            className="w-full mt-1"
            data-testid="submit-sit-button"
          >
            {isSaving ? 'Saving…' : isEditMode ? 'Save Changes' : 'Log Sit'}
          </Button>
        </form>
      </div>
    </div>
  );
}
